function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useContext, useId, useState } from "react";
import { Modal as ChakraModal, ModalOverlay, ModalContent, ModalHeader, ModalFooter as ChakraModalFooter, ModalBody, ModalCloseButton as ChakraModalCloseButton } from "@chakra-ui/react";
import { Stack } from "../layout";
import { createContext } from "../utils";
import { getTypographyStyles } from "../typography";
const ModalContext = /*#__PURE__*/ React.createContext({});
const Modal = (props)=>{
    const variant = props.variant || 'foundation';
    const defaultProps = {
        size: 'xl'
    };
    if (variant === 'foundation') {
        defaultProps.isCentered = true;
    }
    return /*#__PURE__*/ _jsx(ModalContext.Provider, {
        value: variant,
        children: /*#__PURE__*/ _jsx(ChakraModal, _object_spread({}, defaultProps, props))
    });
};
export const [ModalStateContextProvider, useModalState] = createContext();
export const ModalStateProvider = ({ children })=>{
    const [current, setCurrent] = useState(null);
    const onOpenCallback = useCallback((name)=>{
        setCurrent(name);
    }, [
        setCurrent
    ]);
    const onCloseCallback = useCallback((name)=>{
        if (name === current) {
            setCurrent(null);
        }
    }, [
        setCurrent,
        current
    ]);
    return /*#__PURE__*/ _jsx(ModalStateContextProvider, {
        value: {
            onOpen: onOpenCallback,
            onClose: onCloseCallback,
            current
        },
        children: children
    });
};
/**
 * Roughly equivalent to useDisclosure but it's centralised via a ModalStateProvider
 */ export const useModal = (options)=>{
    var _options;
    const { current, onClose, onOpen } = useModalState();
    const uid = useId();
    var _options_id;
    return {
        isOpen: current === ((_options_id = (_options = options) === null || _options === void 0 ? void 0 : _options.id) !== null && _options_id !== void 0 ? _options_id : uid),
        onClose: ()=>onClose(uid),
        onOpen: ()=>onOpen(uid)
    };
};
const ThemedModal = {
    parts: [],
    variants: {
        foundation: ()=>({
                header: _object_spread_props(_object_spread({}, getTypographyStyles('heading.s')), {
                    display: 'flex',
                    height: 'size.xl',
                    justifyContent: 'space-between',
                    paddingStart: 'spacing.l',
                    paddingEnd: 'spacing.m',
                    textAlign: 'left'
                }),
                closeButton: {
                    variant: 'ghost'
                },
                body: _object_spread_props(_object_spread({}, getTypographyStyles('body.s.default')), {
                    padding: 'spacing.l',
                    textAlign: 'left',
                    w: '100%'
                }),
                dialog: {
                    background: 'background.neutral.secondary.default',
                    borderRadius: 's',
                    paddingBottom: 'spacing.l',
                    paddingTop: 'spacing.xs'
                },
                footer: {
                    alignItems: 'flex-start',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: 'spacing.l'
                }
            })
    },
    defaultProps: {
        variant: 'foundation'
    }
};
const ModalFooter = (_param)=>{
    var { children } = _param, props = _object_without_properties(_param, [
        "children"
    ]);
    return /*#__PURE__*/ _jsx(ChakraModalFooter, _object_spread_props(_object_spread({}, props), {
        children: /*#__PURE__*/ _jsx(Stack, {
            alignItems: "center",
            direction: "row",
            flexWrap: "wrap-reverse",
            justifyContent: "center",
            w: "100%",
            // Used to quickfix top / bottom margins when the flex item wraps
            spacing: 0,
            gap: {
                base: 'spacing.l',
                md: 'spacing.2xl'
            },
            children: children
        })
    }));
};
const ModalCloseButton = (props)=>{
    const variant = useContext(ModalContext);
    const defaultProps = {};
    if (variant === 'foundation') {
        defaultProps.size = 'lg';
    }
    return /*#__PURE__*/ _jsx(ChakraModalCloseButton, _object_spread({}, defaultProps, props));
};
export { Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, ThemedModal };

//# sourceMappingURL=Modal.js.map